/**
 * MyBookings Page
 * Displays user's upcoming and past consultation bookings
 */

import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Paper,
  Typography,
  Box,
  Card,
  CardContent,
  Grid,
  Chip,
  Button,
  CircularProgress,
  Divider,
  Alert
} from '@mui/material';
import {
  AccessTime,
  CalendarToday,
  Person,
  CheckCircle,
  Cancel,
  HourglassEmpty,
  Chat as ChatIcon
} from '@mui/icons-material';
import { fetchMyBookings, cancelBooking } from '../store/slices/bookingSlice';
import { formatSlotTime } from '../utils/slotUtils';
import { formatDateForDisplay } from '../utils/dateUtils';

const MyBookings = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { myBookings, loading, error } = useSelector(state => state.booking);
  const [cancellingId, setCancellingId] = useState(null);

  useEffect(() => {
    dispatch(fetchMyBookings());
  }, [dispatch]);

  // Debug: Log bookings data
  useEffect(() => {
    if (myBookings && myBookings.length > 0) {
      console.log('[MyBookings] Bookings data:', myBookings);
      myBookings.forEach(booking => {
        console.log(`[MyBookings] Booking ${booking.id}:`, {
          status: booking.status,
          consultation: booking.consultation,
          canJoin: canJoinChat(booking)
        });
      });
    }
  }, [myBookings]);

  const handleCancelBooking = async (bookingId) => {
    if (window.confirm('Are you sure you want to cancel this booking?')) {
      setCancellingId(bookingId);
      try {
        await dispatch(cancelBooking({ bookingId })).unwrap();
        // Refresh bookings list
        dispatch(fetchMyBookings());
      } catch (err) {
        console.error('Failed to cancel booking:', err);
      } finally {
        setCancellingId(null);
      }
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'confirmed':
        return <CheckCircle sx={{ color: 'success.main' }} />;
      case 'cancelled':
        return <Cancel sx={{ color: 'error.main' }} />;
      case 'completed':
        return <CheckCircle sx={{ color: 'primary.main' }} />;
      default:
        return <HourglassEmpty sx={{ color: 'warning.main' }} />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'confirmed':
        return 'success';
      case 'cancelled':
        return 'error';
      case 'completed':
        return 'primary';
      default:
        return 'warning';
    }
  };

  const canCancelBooking = (booking) => {
    if (booking.status !== 'confirmed') return false;
    // Check if booking is in the future
    const bookingDate = new Date(booking.slot?.date);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return bookingDate >= today;
  };

  const handleJoinChat = (booking) => {
    // Check if consultation exists and has been created
    // Backend returns consultation as nested object: booking.consultation.id
    const consultationId = booking.consultation?.id || booking.consultation_id;
    if (consultationId) {
      navigate(`/chat/${consultationId}`);
    } else {
      alert('The consultation session has not started yet. Please wait until the scheduled time.');
    }
  };

  const canJoinChat = (booking) => {
    // Can join if consultation was created (auto-session service ran)
    // Backend returns consultation as nested object with id and status
    const hasConsultation = booking.consultation?.id || booking.consultation_id;
    const consultationStatus = booking.consultation?.status;
    // Can join if consultation exists and is ongoing (or status is not 'pending')
    return booking.status === 'confirmed' && hasConsultation && consultationStatus !== 'pending';
  };

  const renderBookingCard = (booking) => {
    const slot = booking.slot || {};
    const timeDisplay = slot.start_time && slot.end_time
      ? formatSlotTime(slot.start_time, slot.end_time)
      : 'Time not available';
    const dateDisplay = slot.date ? formatDateForDisplay(slot.date) : 'Date not available';

    return (
      <Grid item xs={12} key={booking.id}>
        <Card sx={{ mb: 2 }}>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <Typography variant="h6">
                    Consultation Booking
                  </Typography>
                  <Chip
                    icon={getStatusIcon(booking.status)}
                    label={booking.status?.toUpperCase() || 'PENDING'}
                    color={getStatusColor(booking.status)}
                    size="small"
                    sx={{ ml: 2 }}
                  />
                </Box>
                <Typography variant="caption" color="textSecondary">
                  Booking ID: {booking.id}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', gap: 1 }}>
                {canJoinChat(booking) && (
                  <Button
                    variant="contained"
                    color="primary"
                    size="small"
                    onClick={() => handleJoinChat(booking)}
                    startIcon={<ChatIcon />}
                  >
                    Join Chat
                  </Button>
                )}
                {canCancelBooking(booking) && (
                  <Button
                    variant="outlined"
                    color="error"
                    size="small"
                    onClick={() => handleCancelBooking(booking.id)}
                    disabled={cancellingId === booking.id}
                    startIcon={cancellingId === booking.id ? <CircularProgress size={16} /> : null}
                  >
                    {cancellingId === booking.id ? 'Cancelling...' : 'Cancel'}
                  </Button>
                )}
              </Box>
            </Box>

            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <CalendarToday sx={{ mr: 1, color: 'primary.main' }} />
                  <Box>
                    <Typography variant="caption" color="textSecondary">
                      Date
                    </Typography>
                    <Typography variant="body1" fontWeight="bold">
                      {dateDisplay}
                    </Typography>
                  </Box>
                </Box>
              </Grid>

              <Grid item xs={12} md={4}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <AccessTime sx={{ mr: 1, color: 'primary.main' }} />
                  <Box>
                    <Typography variant="caption" color="textSecondary">
                      Time
                    </Typography>
                    <Typography variant="body1" fontWeight="bold">
                      {timeDisplay}
                    </Typography>
                  </Box>
                </Box>
              </Grid>

              <Grid item xs={12} md={4}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Person sx={{ mr: 1, color: 'primary.main' }} />
                  <Box>
                    <Typography variant="caption" color="textSecondary">
                      Astrologer
                    </Typography>
                    <Typography variant="body1" fontWeight="bold">
                      {booking.astrologer_name || 'Not specified'}
                    </Typography>
                  </Box>
                </Box>
              </Grid>
            </Grid>

            {booking.user_notes && (
              <>
                <Divider sx={{ my: 2 }} />
                <Box>
                  <Typography variant="caption" color="textSecondary">
                    Your Notes:
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 0.5 }}>
                    {booking.user_notes}
                  </Typography>
                </Box>
              </>
            )}

            {booking.created_at && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="caption" color="textSecondary">
                  Booked on: {new Date(booking.created_at).toLocaleString()}
                </Typography>
              </Box>
            )}
          </CardContent>
        </Card>
      </Grid>
    );
  };

  const upcomingBookings = myBookings.filter(b => {
    if (b.status === 'cancelled' || b.status === 'completed') return false;
    const bookingDate = new Date(b.slot?.date);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return bookingDate >= today;
  });

  const pastBookings = myBookings.filter(b => {
    if (b.status === 'cancelled') return true;
    if (b.status === 'completed') return true;
    const bookingDate = new Date(b.slot?.date);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return bookingDate < today;
  });

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Paper sx={{ p: 4 }}>
        <Typography variant="h4" gutterBottom>
          My Bookings
        </Typography>
        <Typography variant="body1" color="textSecondary" sx={{ mb: 3 }}>
          View and manage your consultation bookings
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error?.message || error?.error || (typeof error === 'string' ? error : 'Failed to load bookings')}
          </Alert>
        )}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
            <CircularProgress />
            <Typography variant="body1" sx={{ ml: 2 }}>
              Loading your bookings...
            </Typography>
          </Box>
        ) : (
          <>
            {upcomingBookings.length > 0 && (
              <Box sx={{ mb: 4 }}>
                <Typography variant="h5" gutterBottom>
                  Upcoming Consultations
                </Typography>
                <Grid container>
                  {upcomingBookings.map(booking => renderBookingCard(booking))}
                </Grid>
              </Box>
            )}

            {pastBookings.length > 0 && (
              <Box>
                <Typography variant="h5" gutterBottom>
                  Past & Cancelled Bookings
                </Typography>
                <Grid container>
                  {pastBookings.map(booking => renderBookingCard(booking))}
                </Grid>
              </Box>
            )}

            {myBookings.length === 0 && !loading && (
              <Box sx={{ textAlign: 'center', py: 6 }}>
                <Typography variant="h6" color="textSecondary" gutterBottom>
                  No bookings yet
                </Typography>
                <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
                  Book a consultation to get started
                </Typography>
                <Button
                  variant="contained"
                  href="/book-consultation"
                >
                  Book Consultation
                </Button>
              </Box>
            )}
          </>
        )}
      </Paper>
    </Container>
  );
};

export default MyBookings;
